import {
  buildTrailDifficultyMeter,
  type TrailDifficultyMeterModel,
} from "@/lib/trail-difficulty";

/**
 * The GreenPak trail difficulty meter.
 *
 * FIVE RISING BARS, not an arc or a gauge. The shape is a ridge line read
 * left to right — the thing the rating is actually about — and it is drawn in
 * the site's own gold/green tokens at the site's own type scale. It is
 * deliberately not the segmented arch used by the reference screenshot.
 *
 * NEVER COLOUR-ONLY. Three independent channels carry the rating: bar height
 * rises with the level, the current bar is capped and outlined so it is
 * distinguishable in greyscale, and the number and the word are always
 * present as text. The bars themselves are aria-hidden and a single
 * visually-hidden sentence carries the whole rating to a screen reader, so
 * assistive tech never has to reconstruct meaning from five nameless divs.
 *
 * Colour note: --cta, not --accent. In light theme --accent resolves to navy,
 * which is the site's "ink" colour, not its action colour — the same trap
 * that made the map's filter chips unreadable.
 */

const BAR_HEIGHTS = ["h-2", "h-3", "h-4", "h-5", "h-6"] as const;

function Bars({ model, size }: { model: TrailDifficultyMeterModel; size: "sm" | "md" }) {
  const width = size === "sm" ? "w-1.5" : "w-2";
  const gap = size === "sm" ? "gap-[3px]" : "gap-1";
  return (
    <span className={`inline-flex items-end ${gap}`} aria-hidden="true">
      {model.segments.map((segment) => {
        const height = BAR_HEIGHTS[segment.index - 1];
        if (segment.state === "empty") {
          return (
            <span
              key={segment.index}
              className={`${width} ${height} rounded-[2px] bg-[var(--border-subtle)]`}
            />
          );
        }
        if (segment.state === "filled") {
          return (
            <span
              key={segment.index}
              className={`${width} ${height} rounded-[2px] bg-[color-mix(in_srgb,var(--cta)_55%,transparent)]`}
            />
          );
        }
        // The current bar: full-strength fill plus a ring, so the rating's
        // position survives greyscale, low contrast and colour blindness.
        return (
          <span
            key={segment.index}
            className={`${width} ${height} rounded-[2px] bg-[var(--cta)] outline outline-2 outline-offset-[2px] outline-[color-mix(in_srgb,var(--cta)_45%,transparent)]`}
          />
        );
      })}
    </span>
  );
}

/**
 * Card variant — bars, the level, and the word. Compact enough for a grid
 * tile but never reduced to just the bars: a rating nobody can read is worse
 * than no rating.
 */
export function TrailDifficultyMeterCompact({
  difficulty,
  note,
}: {
  difficulty: number | null;
  note?: string | null;
}) {
  const model = buildTrailDifficultyMeter({ level: difficulty, note });
  // Unrated trails render nothing. An empty meter reads as "Easy".
  if (!model) return null;

  return (
    <span className="inline-flex items-center gap-2.5">
      <Bars model={model} size="sm" />
      <span className="text-[0.78rem] font-semibold tracking-wide text-[var(--foreground)]">
        {model.label}
      </span>
      <span className="text-[0.72rem] font-medium tabular-nums text-[var(--muted)]">
        {model.position}
      </span>
      <span className="sr-only">{model.accessibleText}</span>
    </span>
  );
}

/**
 * Facts-panel variant — the full statement, with the trail's own one-line
 * explanation when an editor wrote one.
 */
export function TrailDifficultyMeter({
  difficulty,
  note,
  className = "",
}: {
  difficulty: number | null;
  note?: string | null;
  className?: string;
}) {
  const model = buildTrailDifficultyMeter({ level: difficulty, note });
  if (!model) return null;

  return (
    <div className={className}>
      <p className="pbd-eyebrow m-0">GreenPak difficulty</p>
      <div className="mt-2.5 flex items-end gap-3">
        <Bars model={model} size="md" />
        <span className="font-[family-name:var(--font-display)] text-[1.6rem] leading-none font-medium tabular-nums text-[var(--foreground)]">
          {model.position}
        </span>
      </div>
      <p className="mt-2.5 mb-0 text-[1.05rem] font-semibold text-[var(--foreground)]">
        {model.label}
      </p>
      {model.note ? (
        <p className="mt-1.5 mb-0 text-sm leading-relaxed text-[var(--muted)]">{model.note}</p>
      ) : null}
      <p className="sr-only">{model.accessibleText}</p>
    </div>
  );
}
